import React, { useEffect, useState } from 'react';
import ImageUploader from './components/ImageUploader';
import './TestimonialForm.css';
import FormActions from './components/FormActions';
import useConfirmNavigation from '../../hooks/useConfirmNavigation';

const defaultTestimonial = {
  name: '',
  handle: '',
  role: '',
  company: '',
  location: '',
  project: '',
  budget: '',
  timeframe: '',
  rating: 5,
  sortOrder: 0,
  testimonial: '',
  image: '',
  skills: [],
  pageAliases: []
};

const textFields = [
  'name',
  'handle',
  'role',
  'company',
  'location',
  'project',
  'budget',
  'timeframe',
  'testimonial',
  'image'
];

const buildFormState = (data = {}) => {
  const safeData = data && typeof data === 'object' ? data : {};
  const normalizedTextValues = textFields.reduce((acc, key) => {
    const fallback = defaultTestimonial[key] || '';
    acc[key] = typeof safeData[key] === 'string' ? safeData[key] : fallback;
    return acc;
  }, {});

  return {
    ...defaultTestimonial,
    ...safeData,
    ...normalizedTextValues,
    rating: Number(safeData.rating ?? defaultTestimonial.rating),
    sortOrder: Number(safeData.sortOrder ?? defaultTestimonial.sortOrder),
    skills: Array.isArray(safeData.skills) ? safeData.skills : defaultTestimonial.skills,
    pageAliases: Array.isArray(safeData.pageAliases) ? safeData.pageAliases : defaultTestimonial.pageAliases
  };
};

const listToInputValue = (list) => (Array.isArray(list) ? list.join(', ') : '');

const normalizePagePath = (value) => {
  if (!value) return '';
  let path = value.trim();
  if (!path) return '';
  if (!path.startsWith('/')) {
    path = `/${path}`;
  }
   path = path.replace(/\/+/g, '/');
   path = path.replace(/\/+$/, '');
  if (!path) return '/';
  return path === '/' ? '/' : path.toLowerCase();
};

const splitList = (value = '', { normalizePath = false } = {}) =>
  value
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => (normalizePath ? normalizePagePath(part) : part))
    .filter(Boolean);

const TestimonialForm = ({ initialData, onSubmit, onCancel, isSaving }) => {
  const [formData, setFormData] = useState(() => buildFormState(initialData));
  const [skillsInput, setSkillsInput] = useState(listToInputValue(initialData?.skills));
  const [pageAliasesInput, setPageAliasesInput] = useState(listToInputValue(initialData?.pageAliases));
  const [activeTab, setActiveTab] = useState('client');
  const confirmationMessage = 'You have unsaved testimonial changes. Leave this form?';
  const submitLabel = 'Save Testimonial';

  useConfirmNavigation(!isSaving, confirmationMessage);

  useEffect(() => {
    const nextForm = buildFormState(initialData);
    const nextSkills = listToInputValue(initialData?.skills);
    const nextAliases = listToInputValue(initialData?.pageAliases);
    setFormData(nextForm);
    setSkillsInput(nextSkills);
    setPageAliasesInput(nextAliases);
  }, [initialData]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const sanitize = (value) => (typeof value === 'string' ? value.trim() : '');
    const payload = {
      name: sanitize(formData.name),
      handle: sanitize(formData.handle),
      role: sanitize(formData.role),
      company: sanitize(formData.company),
      location: sanitize(formData.location),
      project: sanitize(formData.project),
      budget: sanitize(formData.budget),
      timeframe: sanitize(formData.timeframe),
      rating: Math.min(5, Math.max(1, Number(formData.rating) || 5)),
      sortOrder: Number(formData.sortOrder) || 0,
      testimonial: sanitize(formData.testimonial),
      image: sanitize(formData.image),
      skills: splitList(skillsInput),
      pageAliases: splitList(pageAliasesInput, { normalizePath: true })
    };

    onSubmit(payload);
  };

  const handleCancelClick = () => {
    if (!window.confirm(confirmationMessage)) {
      return;
    }
    onCancel();
  };

  return (
    <form className="testimonial-form" onSubmit={handleSubmit}>
      <FormActions sticky isSaving={isSaving} onCancel={handleCancelClick} submitLabel={submitLabel} />

      <div className="form-tabs" role="tablist">
        {[
          { key: 'client', label: 'Client Details' },
          { key: 'project', label: 'Project Snapshot' },
          { key: 'skills', label: 'Skills & Pages' },
          { key: 'avatar', label: 'Avatar' }
        ].map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`tab-btn ${activeTab === tab.key ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
            role="tab"
            aria-selected={activeTab === tab.key}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <section className="form-sections">
        {activeTab === 'client' && (
          <div className="form-card">
            <h2>Client Details</h2>
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="name">Name *</label>
                <input id="name" name="name" value={formData.name} onChange={handleChange} required />
              </div>
              <div className="form-group">
                <label htmlFor="handle">Handle / Username</label>
                <input id="handle" name="handle" value={formData.handle} onChange={handleChange} placeholder="@client" />
              </div>
              <div className="form-group">
                <label htmlFor="role">Role</label>
                <input id="role" name="role" value={formData.role} onChange={handleChange} placeholder="Founder" />
              </div>
              <div className="form-group">
                <label htmlFor="company">Company</label>
                <input id="company" name="company" value={formData.company} onChange={handleChange} placeholder="IfStatic" />
              </div>
              <div className="form-group">
                <label htmlFor="location">Location</label>
                <input id="location" name="location" value={formData.location} onChange={handleChange} placeholder="New Delhi, India" />
              </div>
            </div>
          </div>
        )}

        {activeTab === 'project' && (
          <div className="form-card">
            <h2>Project Snapshot</h2>
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="project">Project Name</label>
                <input id="project" name="project" value={formData.project} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label htmlFor="budget">Budget</label>
                <input id="budget" name="budget" value={formData.budget} onChange={handleChange} placeholder="$5,000" />
              </div>
              <div className="form-group">
                <label htmlFor="timeframe">Timeframe</label>
                <input id="timeframe" name="timeframe" value={formData.timeframe} onChange={handleChange} placeholder="3 months" />
              </div>
              <div className="form-group">
                <label htmlFor="rating">Rating (1-5)</label>
                <input
                  id="rating"
                  name="rating"
                  type="number"
                  min="1"
                  max="5"
                  step="1"
                  value={formData.rating}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label htmlFor="sortOrder">Display Order</label>
                <input
                  id="sortOrder"
                  name="sortOrder"
                  type="number"
                  min="0"
                  value={formData.sortOrder}
                  onChange={handleChange}
                />
                <span className="field-hint">Lower numbers show first.</span>
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="testimonial">Testimonial *</label>
              <textarea
                id="testimonial"
                name="testimonial"
                value={formData.testimonial}
                onChange={handleChange}
                required
                rows="4"
              />
            </div>
          </div>
        )}

        {activeTab === 'skills' && (
          <div className="form-card">
            <h2>Skills & Target Pages</h2>
            <div className="form-group">
              <label htmlFor="skills">Skills / Tags (comma separated)</label>
              <input
                id="skills"
                name="skills"
                value={skillsInput}
                onChange={(event) => setSkillsInput(event.target.value)}
                placeholder="React Native, Flutter"
              />
            </div>
            <div className="form-group">
              <label htmlFor="pageAliases">
                Page Paths (comma separated)
                <span className="field-hint">Use full paths like /services/app-development or / for the homepage.</span>
              </label>
              <input
                id="pageAliases"
                name="pageAliases"
                value={pageAliasesInput}
                onChange={(event) => setPageAliasesInput(event.target.value)}
                placeholder="/services/app-development, /about"
              />
            </div>
          </div>
        )}

        {activeTab === 'avatar' && (
          <div className="form-card">
            <h2>Avatar</h2>
            <ImageUploader value={formData.image} onChange={(url) => setFormData((prev) => ({ ...prev, image: url }))} />
          </div>
        )}
      </section>

      <FormActions variant="footer" isSaving={isSaving} onCancel={handleCancelClick} submitLabel={submitLabel} />
    </form>
  );
};

export default TestimonialForm;
